<script setup lang="ts">
    import { computed, toRef } from 'vue'

    import type { MeetingStarterViewModel } from '@Evaluation/Estimations/Components/MeetingStarter'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: MeetingStarterViewModel
    }>()

    const emit = defineEmits<{
        (e: 'startMeeting'): void
        (e: 'openSurvey'): void
        (e: 'closeSurvey'): void
        (e: 'toggleAutoReveal'): void
        (e: 'toggleNamesRevealed'): void
        (e: 'showAccessLinks'): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const surveyStarted = computed(() => vm.value.surveyStarted)
    const autoRevealEnabled = computed(() => vm.value.autoRevealEnabled)
    const namesRevealed = computed(() => vm.value.namesRevealed)

    // ---------------- actions ----------------

    function startMeeting()
    {
        emit('startMeeting')
    }

    function openSurvey()
    {
        emit('openSurvey')
    }

    function closeSurvey()
    {
        emit('closeSurvey')
    }

    function toggleAutoReveal()
    {
        emit('toggleAutoReveal')
        vm.value.autoRevealEnabled = !vm.value.autoRevealEnabled
    }

    function toggleNamesRevealed()
    {
        emit('toggleNamesRevealed')
        vm.value.namesRevealed = !vm.value.namesRevealed
    }

    function showAccessLinks()
    {
        emit('showAccessLinks')
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Set up your meeting​</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <p class="lead">
                            Before you start your real-time meeting, let’s make sure everything is ready.
                            <strong>You can change any of these settings during the meeting.</strong>
                        </p><br />

                        <table class="table">
                            <tbody>
                                <tr v-if="surveyStarted">
                                    <td>
                                        <h5>Survey is open</h5>
                                        <p>Your team can change their answers to <strong>ANY question</strong> during the meeting. </p>
                                    </td>
                                    <td class="align-middle">
                                        <button class="btn btn-outline-success btn-block text-nowrap" @click="closeSurvey">Close survey</button>
                                    </td>
                                </tr>
                                <tr v-if="!surveyStarted">
                                    <td>
                                        <h5>Survey is closed</h5>
                                        <p>Your team will only be able to change their answers for the <strong>question you’re on.</strong></p>
                                    </td>
                                    <td class="align-middle">
                                        <button class="btn btn-outline-secondary btn-block text-nowrap" @click="openSurvey">Open survey</button>
                                    </td>
                                </tr>
                                <tr v-if="autoRevealEnabled">
                                    <td>
                                        <h5>Auto reveal is ON</h5>
                                        <p>When you move from question to question, your meeting participants WILL be able to see each others’ votes.​</p>
                                    </td>
                                    <td class="align-middle">
                                        <button class="btn btn-outline-secondary btn-block text-nowrap" @click="toggleAutoReveal">Turn off</button>
                                    </td>
                                </tr>
                                <tr v-if="!autoRevealEnabled">
                                    <td>
                                        <h5>Auto reveal is OFF</h5>
                                        <p>When you move to the next question, we will automatically hide everyone’s votes. This can help reduce “follow my leader” bias and “group think.”</p>
                                    </td>
                                    <td class="align-middle">
                                        <button class="btn btn-outline-secondary btn-block text-nowrap" @click="toggleAutoReveal">Turn on</button>
                                    </td>
                                </tr>
                                <tr v-if="namesRevealed">
                                    <td>
                                        <h5>Anonymous mode is OFF</h5>
                                        <p>When sharing votes, names WILL be displayed.​</p>
                                    </td>
                                    <td class="align-middle">
                                        <button class="btn btn-outline-secondary btn-block text-nowrap" @click="toggleNamesRevealed">Turn on</button>
                                    </td>
                                </tr>
                                <tr v-if="!namesRevealed">
                                    <td>
                                        <h5>Anonymous mode is ON</h5>
                                        <p>When sharing votes, names WILL NOT be displayed.​</p>
                                    </td>
                                    <td class="align-middle">
                                        <button class="btn btn-outline-secondary btn-block text-nowrap" @click="toggleNamesRevealed">Turn off</button>
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <h5>Access links</h5>
                                        <p>These links will let your participants join the meeting with just one click. Why not (re)send them now?​</p>
                                    </td>
                                    <td class="align-middle">
                                        <button class="btn btn-outline-secondary btn-block text-nowrap" @click="showAccessLinks">Access links</button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="modal-footer">
                        <button @click="startMeeting" type="button" class="btn btn-primary">Start the meeting</button>
                        <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
